/**
 * Quote history: "What's happening with Q-1031?" / "Has she opened her quote?"
 * (docs/intelligent-chat-answers-plan.md, "Build plan for the designed
 * components", wave 2).
 *
 * A vertical history of ONE quote: created, sent (and how, where the comms log
 * knows), each time the customer's page was opened, accepted or declined, and
 * a "not opened for N days" line only when that is a fact. Then two taps: ask
 * Mucka to follow up (in words, as the owner) and open the quote.
 *
 * 🚨 Deliberately NOT on the board's design: no pre-written follow-up words and
 * no direct "Send follow-up". The follow-up is staged by the ordinary send path
 * when the owner asks, and THAT card shows its own words. A block printing
 * words the staged card then differs from would break the trust model — the
 * owner would approve one sentence having been shown another.
 *
 * 🚨 "Not opened" is only ever said about a quote that went out WITH a page to
 * open. A quote sent as a bare PDF has no page, so silence from the page is a
 * fact about us, not about the customer — `openedState` in
 * `src/lib/document-views/summary.ts` refuses that claim and this follows it
 * exactly, by calling it.
 *
 * No money on the block at all, and nothing naming the customer: the reply's
 * sentence names them, and the block is the dates.
 *
 * Pure and directive-free, so the renderer, the route and the grader can all
 * import it (anti-pattern #18). Labels are built HERE, on the server, at the
 * moment of the answer, so a stored reply redraws what was true then.
 */
import { openedState, type OpenedState } from '@/lib/document-views/summary';
import { ukDate } from '@/lib/utils/date-format';
import { londonTodayIso } from '@/lib/on-time/local-time';

/** What a dot on the rail means — its colour, nothing else. */
export type QuoteHistoryTone = 'plain' | 'seen' | 'good' | 'bad' | 'warn';

export type QuoteHistoryEventKind =
  | 'created'
  | 'sent'
  | 'followed_up'
  | 'replied'
  | 'opened'
  | 'opened_more'
  | 'change_sent'
  | 'change_accepted'
  | 'change_declined'
  | 'deposit_paid'
  | 'accepted'
  | 'declined'
  | 'expired'
  | 'quiet'
  | 'more';

export interface QuoteHistoryEvent {
  kind: QuoteHistoryEventKind;
  /** "Sent by email", "Opened again" — built here, never by the model. */
  label: string;
  /** "Mon 28 Sep, 16:10", London. Null when the moment was never recorded. */
  when: string | null;
  tone: QuoteHistoryTone;
}

export interface QuoteHistoryBlock {
  kind: 'quote-history';
  quoteId: string;
  reference: string;
  /** `quotes.status`, as stored. */
  status: string;
  events: QuoteHistoryEvent[];
  /** `openedState(...)` — which of the four honest answers this quote has. */
  opened: OpenedState['kind'];
  /** Debounced page opens (one per IP per hour, at write time). */
  opens: number;
  /** The one honest sentence under the rail, or null. */
  note: string | null;
  /** Only an open, sent quote can usefully be followed up. */
  canFollowUp: boolean;
  /** Where "Open Q-1031" goes. */
  path: string;
}

/** One `quote_share_events` row, reduced. */
export interface QuoteShareEventFact {
  type: string;
  /** ISO timestamp. */
  at: string;
}

/** One `client_communications` row about this quote, reduced. */
export interface QuoteCommFact {
  channel: string;
  direction: string;
  surface: string | null;
  status: string | null;
  /** ISO timestamp — `occurred_at`, else `created_at`. */
  at: string;
}

/** Everything the history is built from — read by `quote-history-data.ts`. */
export interface QuoteHistoryFacts {
  quoteId: string;
  reference: string | null;
  status: string;
  createdAt: string;
  sentAt: string | null;
  /** YYYY-MM-DD. */
  validUntil: string | null;
  /** Set only by the public accept route, so it means "accepted online". */
  acceptedAt: string | null;
  /** True when the customer was ever given a page for this quote. */
  hasCustomerLink: boolean;
  shareEvents: readonly QuoteShareEventFact[];
  comms: readonly QuoteCommFact[];
}

/** Opens shown one by one; past this the middle ones fold into one row. */
export const QUOTE_HISTORY_OPENS_SHOWN = 5;
/** Rows on the rail before the oldest are folded into "N earlier not shown". */
export const QUOTE_HISTORY_MAX_EVENTS = 12;
/** How long an open quote has to sit unopened before the rail says so in amber. */
export const QUOTE_QUIET_DAYS = 3;

/** Said under the rail when the quote went out with no page to open. */
export const UNTRACKED_NOTE = "Sent as an attachment, so Mucka can't see if it's been opened.";

/** Two sends this close together are one send seen twice (`sent_at` and the log row). */
const SAME_SEND_MS = 15 * 60_000;
const DAY_MS = 86_400_000;

function ms(iso: string): number {
  return Date.parse(iso);
}

/** "Mon 28 Sep, 16:10", London, through `ukDate` (anti-pattern #41). */
export function quoteHistoryWhen(iso: string): string {
  return `${ukDate(iso, { weekday: 'short', day: 'numeric', month: 'short' })}, ${ukDate(iso, { hour: '2-digit', minute: '2-digit', hour12: false })}`;
}

/** "Fri 2 Oct" — a calendar day (a `date` column), the same day everywhere. */
function dayOnly(isoOrYmd: string): string {
  const d = /^\d{4}-\d{2}-\d{2}$/.test(isoOrYmd) ? `${isoOrYmd}T12:00:00Z` : isoOrYmd;
  return ukDate(d, { weekday: 'short', day: 'numeric', month: 'short' });
}

function how(channel: string | null): string {
  switch (channel) {
    case 'email': return ' by email';
    case 'sms': return ' by text';
    case 'whatsapp': return ' on WhatsApp';
    default: return '';
  }
}

/** The comms log's word for a send that never reached anybody. */
function undelivered(status: string | null): string | null {
  if (status === 'undelivered') return "didn't arrive";
  if (status === 'failed' || status === 'suppressed') return "didn't go out";
  return null;
}

interface Dated { at: string | null; event: QuoteHistoryEvent }

function ev(kind: QuoteHistoryEventKind, label: string, at: string | null, tone: QuoteHistoryTone): Dated {
  return { at, event: { kind, label, when: at ? quoteHistoryWhen(at) : null, tone } };
}

/**
 * The block. `now` is passed in so this stays pure; it is the moment the
 * answer was given, and the only thing the "not opened for N days" line counts
 * from.
 */
export function buildQuoteHistoryBlock(facts: QuoteHistoryFacts, now: Date): QuoteHistoryBlock {
  const reference = facts.reference?.trim() || 'this quote';
  const dated: Dated[] = [ev('created', 'Created', facts.createdAt, 'plain')];

  // ── Sends ── `sent_at` says WHEN; the comms log, where it has a row, says
  // HOW. The same send seen by both is one send. The log only began writing
  // quote rows partway through 2026, so an older quote's send has no "how",
  // and says only "Sent" rather than guess.
  const sendRows = facts.comms
    .filter(c => c.direction === 'outbound' && c.surface === 'quote')
    .slice()
    .sort((a, b) => ms(a.at) - ms(b.at));
  const sends: { at: string; channel: string | null; status: string | null }[] =
    sendRows.map(c => ({ at: c.at, channel: c.channel, status: c.status }));
  if (facts.sentAt && !sends.some(s => Math.abs(ms(s.at) - ms(facts.sentAt!)) <= SAME_SEND_MS)) {
    sends.push({ at: facts.sentAt, channel: null, status: null });
  }
  sends.sort((a, b) => ms(a.at) - ms(b.at));
  sends.forEach((s, i) => {
    const failed = undelivered(s.status);
    const base = `${i === 0 ? 'Sent' : 'Sent again'}${how(s.channel)}`;
    dated.push(ev('sent', failed ? `${base}, but it ${failed}` : base, s.at, failed ? 'bad' : 'plain'));
  });
  if (!sends.length && facts.status === 'sent') {
    // Marked sent with no moment recorded — an import, usually. Only for
    // 'sent': an accepted quote may never have been sent at all (a Share link
    // handed over in person), and saying it was would be invented.
    dated.push(ev('sent', 'Marked as sent, no date recorded', null, 'plain'));
  }

  // ── Chases and replies ──
  for (const c of facts.comms) {
    if (c.direction === 'outbound' && c.surface === 'quote_chase') {
      const failed = undelivered(c.status);
      const base = `Followed up${how(c.channel)}`;
      dated.push(ev('followed_up', failed ? `${base}, but it ${failed}` : base, c.at, failed ? 'bad' : 'plain'));
    } else if (c.direction === 'inbound') {
      const label = c.channel === 'call' ? 'Customer rang' : `Customer replied${how(c.channel)}`;
      dated.push(ev('replied', label, c.at, 'plain'));
    }
  }

  // ── The customer's page ──
  const views = facts.shareEvents.filter(e => e.type === 'viewed').slice().sort((a, b) => ms(a.at) - ms(b.at));
  const shownViews = views.length > QUOTE_HISTORY_OPENS_SHOWN
    ? [views[0], ...views.slice(views.length - (QUOTE_HISTORY_OPENS_SHOWN - 2))]
    : views;
  shownViews.forEach((v, i) => dated.push(ev('opened', i === 0 ? 'Opened' : 'Opened again', v.at, 'seen')));
  if (views.length > QUOTE_HISTORY_OPENS_SHOWN) {
    const folded = views.slice(1, views.length - (QUOTE_HISTORY_OPENS_SHOWN - 2));
    const first = folded[0];
    const last = folded[folded.length - 1];
    dated.push({
      // Sorted just after the first open, which is where these sit.
      at: first.at,
      event: {
        kind: 'opened_more',
        label: `Opened ${folded.length} more time${folded.length === 1 ? '' : 's'}`,
        when: dayOnly(first.at) === dayOnly(last.at) ? dayOnly(first.at) : `${dayOnly(first.at)} to ${dayOnly(last.at)}`,
        tone: 'seen',
      },
    });
  }

  let acceptedOnline = false;
  let declinedOnline = false;
  for (const e of facts.shareEvents) {
    switch (e.type) {
      case 'accepted': acceptedOnline = true; dated.push(ev('accepted', 'Accepted online', e.at, 'good')); break;
      case 'declined': declinedOnline = true; dated.push(ev('declined', 'Declined online', e.at, 'bad')); break;
      case 'variation_sent': dated.push(ev('change_sent', 'Change sent for approval', e.at, 'plain')); break;
      case 'variation_accepted': dated.push(ev('change_accepted', 'Change approved', e.at, 'good')); break;
      case 'variation_declined': dated.push(ev('change_declined', 'Change declined', e.at, 'bad')); break;
      case 'deposit_paid': dated.push(ev('deposit_paid', 'Deposit paid online', e.at, 'good')); break;
      default: break; // viewed (above), deposit_started, plan_taken: not this history's business
    }
  }
  if (facts.status === 'accepted' && !acceptedOnline) {
    // `accepted_at` is written only by the public accept route.
    dated.push(facts.acceptedAt
      ? ev('accepted', 'Accepted online', facts.acceptedAt, 'good')
      : ev('accepted', 'Marked as accepted', null, 'good'));
  }
  if (facts.status === 'declined' && !declinedOnline) {
    dated.push(ev('declined', 'Marked as declined', null, 'bad'));
  }

  // ── Expiry ──
  const todayYmd = londonTodayIso(now);
  const lapsed = facts.status === 'sent' && !!facts.validUntil && facts.validUntil < todayYmd;
  if (facts.status === 'expired' || lapsed) {
    const label = facts.status === 'expired' ? 'Expired' : 'Its valid-until date has passed';
    dated.push({
      at: facts.validUntil ? `${facts.validUntil}T23:59:59Z` : null,
      event: { kind: 'expired', label, when: facts.validUntil ? dayOnly(facts.validUntil) : null, tone: 'warn' },
    });
  }

  // Chronological; an undated fact sits after every dated one.
  dated.sort((a, b) => (a.at === null ? Infinity : ms(a.at)) - (b.at === null ? Infinity : ms(b.at)));
  let events = dated.map(d => d.event);

  // ── Opened, honestly ──
  const state = openedState({
    sentAt: facts.sentAt ?? (sends[0]?.at ?? null),
    views: views.length ? { count: views.length, lastViewedAt: views[views.length - 1].at } : null,
    hasCustomerLink: facts.hasCustomerLink,
  });
  const open = facts.status === 'sent' && !lapsed;
  if (open && (state.kind === 'opened' || state.kind === 'unopened')) {
    const since = state.kind === 'opened' ? state.views.lastViewedAt : (facts.sentAt ?? sends[0]?.at ?? null);
    if (since) {
      const days = Math.floor((now.getTime() - ms(since)) / DAY_MS);
      const asOf = `As of ${quoteHistoryWhen(now.toISOString())}`;
      if (state.kind === 'opened' && days >= QUOTE_QUIET_DAYS) {
        events.push({ kind: 'quiet', label: `Not opened for ${days} days`, when: asOf, tone: 'warn' });
      } else if (state.kind === 'unopened') {
        events.push(days >= QUOTE_QUIET_DAYS
          ? { kind: 'quiet', label: `Not opened in the ${days} days since it was sent`, when: asOf, tone: 'warn' }
          : { kind: 'quiet', label: 'Not opened yet', when: asOf, tone: 'plain' });
      }
    }
  }

  // ── Cap ── keep "Created" and the newest; say how many were folded.
  if (events.length > QUOTE_HISTORY_MAX_EVENTS) {
    const keep = QUOTE_HISTORY_MAX_EVENTS - 2;
    const hidden = events.length - 1 - keep;
    events = [
      events[0],
      { kind: 'more', label: `${hidden} more event${hidden === 1 ? '' : 's'} not shown`, when: null, tone: 'plain' },
      ...events.slice(events.length - keep),
    ];
  }

  const note = facts.status === 'draft' && state.kind !== 'opened'
    ? 'Still a draft. Nothing has gone to the customer.'
    : state.kind === 'untracked'
      ? UNTRACKED_NOTE
      : null;

  return {
    kind: 'quote-history',
    quoteId: facts.quoteId,
    reference,
    status: facts.status,
    events,
    opened: state.kind,
    opens: views.length,
    note,
    canFollowUp: open,
    path: `/quotes?open=${encodeURIComponent(facts.quoteId)}`,
  };
}

/**
 * The sentence appended to the tool RESULT, so the reply can carry the answer
 * on its own (the block is extra). States what is known and, for a quote sent
 * without a page, that "not opened" cannot be said.
 */
export function quoteHistoryResultNote(block: QuoteHistoryBlock): string {
  const lastOpen = [...block.events].reverse().find(e => e.kind === 'opened');
  const opened = block.opened === 'opened'
    ? `The customer's page for ${block.reference} has been opened ${block.opens === 1 ? 'once' : `${block.opens} times`}${lastOpen?.when ? `, last ${lastOpen.when}` : ''}.`
    : block.opened === 'unopened'
      ? `${block.reference} went out with a link and the customer has not opened it.`
      : block.opened === 'untracked'
        ? `${block.reference} went out without a link to open, so nobody can tell whether it has been opened. Say that if asked; never say it has not been opened.`
        : `${block.reference} has not been sent.`;
  return `\n\n${opened} Its history (sent, opened, accepted or declined, with dates) is on screen`
    + `${block.canFollowUp ? ' with a button to ask for a follow-up' : ''}. `
    + 'Do not read the dates out one by one, and do not draft follow-up words unless the owner asks for one.';
}

/** What "Ask Mucka to follow up" says, as the owner. Nothing is sent by the tap. */
export function quoteFollowUpRequest(block: Pick<QuoteHistoryBlock, 'reference'>): string {
  return `Send a follow-up on ${block.reference}`;
}

/** One line for the oversight grader: states and counts, nobody's name. */
export function summariseQuoteHistoryBlock(block: QuoteHistoryBlock): string {
  const seen = block.opened === 'opened'
    ? `opened ${block.opens} time${block.opens === 1 ? '' : 's'}`
    : block.opened === 'unopened'
      ? 'sent with a link, not opened'
      : block.opened === 'untracked'
        ? 'sent without a link, opens unknown'
        : 'not sent';
  return `[quote-history: ${block.reference}, ${block.status}, ${block.events.length} event${block.events.length === 1 ? '' : 's'}, ${seen}${block.canFollowUp ? ', follow-up offered' : ''}]`;
}
